import React, { useState, useEffect } from 'react';
import { Calculator, ArrowRight, TrendingUp, Target, Clock, LineChart, Brain, CheckCircle2, AlertCircle } from 'lucide-react';

interface RoiEstimate {
  yearlyGains: number[];
  cumulativeGains: number[];
  totalCosts: number;
  paybackPeriod: number;
  roi: number;
}

interface IndustryMultiplier {
  sector: string;
  marginMultiplier: number;
  productivityMultiplier: number;
  newRevenueMultiplier: number;
  growthRate: number;
}

const industryMultipliers: IndustryMultiplier[] = [
  {
    sector: "E-commerce",
    marginMultiplier: 0.10,
    productivityMultiplier: 0.15,
    newRevenueMultiplier: 0.12,
    growthRate: 0.05
  },
  {
    sector: "Services B2B",
    marginMultiplier: 0.12,
    productivityMultiplier: 0.18,
    newRevenueMultiplier: 0.15,
    growthRate: 0.10
  },
  {
    sector: "Distribution",
    marginMultiplier: 0.08,
    productivityMultiplier: 0.12,
    newRevenueMultiplier: 0.10,
    growthRate: 0.04
  },
  {
    sector: "Industrie",
    marginMultiplier: 0.10,
    productivityMultiplier: 0.15,
    newRevenueMultiplier: 0.12,
    growthRate: 0.06
  }
];

export function RoiCalculator() {
  const [step, setStep] = useState(1);
  const [revenue, setRevenue] = useState<string>('');
  const [sector, setSector] = useState<string>('');
  const [hoursPerWeek, setHoursPerWeek] = useState<string>('');
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [initialInvestment, setInitialInvestment] = useState<string>('');
  const [annualCost, setAnnualCost] = useState<string>('');
  const [estimate, setEstimate] = useState<RoiEstimate | null>(null);
  const [showResults, setShowResults] = useState(false);

  const steps = [
    {
      title: "Votre profil",
      description: "Commençons par comprendre votre entreprise",
      icon: Target,
      fields: ["sector", "revenue"],
      tips: [
        "Le secteur détermine les multiplicateurs appliqués",
        "Le CA permet d'estimer votre potentiel d'optimisation"
      ]
    },
    {
      title: "Votre temps",
      description: "Évaluons vos gains de productivité",
      icon: Clock,
      fields: ["hoursPerWeek", "hourlyRate"],
      tips: [
        "Considérez toutes les tâches répétitives",
        "Incluez le temps de toute l'équipe",
        "Le coût horaire moyen inclut les charges"
      ]
    },
    {
      title: "Votre investissement",
      description: "Définissons votre budget",
      icon: Brain,
      fields: ["initialInvestment", "annualCost"],
      tips: [
        "Investissement initial : formation et implémentation",
        "Coûts annuels : abonnements et maintenance"
      ]
    }
  ];

  const currentStep = steps[step - 1];

  const isStepValid = () => {
    switch (step) {
      case 1:
        return sector && revenue;
      case 2:
        return hoursPerWeek && hourlyRate;
      case 3:
        return initialInvestment && annualCost;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < steps.length) {
      setStep(step + 1);
    } else if (isStepValid()) {
      calculateRoi();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const SIMULATION_YEARS = 5;

  const calculateRoi = () => {
    const revenueNum = parseFloat(revenue.replace(/[^0-9]/g, ''));
    const hoursNum = parseFloat(hoursPerWeek);
    const rateNum = parseFloat(hourlyRate);
    const investmentNum = parseFloat(initialInvestment.replace(/[^0-9]/g, ''));
    const annualCostNum = parseFloat(annualCost.replace(/[^0-9]/g, ''));

    if (!revenueNum || !sector || !hoursNum || !rateNum || !investmentNum || !annualCostNum) return;

    const multiplier = industryMultipliers.find(m => m.sector === sector);
    if (!multiplier) return;

    // Calcul du gain sur le temps économisé (annuel)
    const timeGains = hoursNum * rateNum * 52;

    // Calcul des gains par année
    const yearlyGains = Array(SIMULATION_YEARS).fill(0).map((_, year) => {
      const baseGain = timeGains;
      const additionalRevenue = revenueNum * multiplier.newRevenueMultiplier * Math.pow(1 + multiplier.growthRate, year);
      return baseGain + additionalRevenue;
    });

    // Calcul des coûts totaux
    const totalCosts = investmentNum + (annualCostNum * SIMULATION_YEARS);

    // Calcul des gains cumulés
    const cumulativeGains = yearlyGains.reduce((acc, gain, i) => {
      const previousTotal = i > 0 ? acc[i - 1] : 0;
      return [...acc, previousTotal + gain];
    }, [] as number[]);

    // Calcul de la période de retour sur investissement
    const paybackPeriod = cumulativeGains.findIndex(gain => gain >= totalCosts) + 1;

    // Calcul du ROI global
    const totalGains = cumulativeGains[SIMULATION_YEARS - 1];
    const roi = ((totalGains - totalCosts) / totalCosts) * 100;

    setEstimate({
      yearlyGains,
      cumulativeGains,
      totalCosts,
      paybackPeriod,
      roi
    });
    setShowResults(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleRevenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '') {
      setRevenue('');
      return;
    }
    const number = parseInt(value);
    setRevenue(new Intl.NumberFormat('fr-FR').format(number));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 w-full mx-auto relative">
      <div className="flex items-center gap-3 mb-8">
        <Calculator className="w-8 h-8 text-orange-500" />
        <h3 className="text-2xl font-bold">Simulateur de ROI sur {SIMULATION_YEARS} ans</h3>
      </div>

      <div className="grid xl:grid-cols-2 gap-16">
        <div>
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center
                    ${i + 1 === step ? 'bg-orange-500 text-white' :
                      i + 1 < step ? 'bg-green-500 text-white' :
                      'bg-gray-200 text-gray-500'}
                  `}>
                    {i + 1 < step ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`w-full h-1 mx-2 ${i + 1 < step ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Current Step Title */}
          <div className="mb-10">
            <h4 className="text-xl font-bold mb-2 flex items-center gap-2">
              {React.createElement(currentStep.icon, { className: "w-6 h-6 text-orange-500" })}
              {currentStep.title}
            </h4>
            <p className="text-gray-600">{currentStep.description}</p>
          </div>

          <div className="space-y-8 mb-10">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Investissement initial (€)
              </label>
              <div className="relative">
                <input
                  type="text"
                  style={{ display: currentStep.fields.includes('initialInvestment') ? 'block' : 'none' }}
                  value={initialInvestment}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '') {
                      setInitialInvestment('');
                      return;
                    }
                    const number = parseInt(value);
                    setInitialInvestment(new Intl.NumberFormat('fr-FR').format(number));
                  }}
                  placeholder="Ex: 5000"
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coût annuel récurrent (€)
              </label>
              <div className="relative">
                <input
                  type="text"
                  style={{ display: currentStep.fields.includes('annualCost') ? 'block' : 'none' }}
                  value={annualCost}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '') {
                      setAnnualCost('');
                      return;
                    }
                    const number = parseInt(value);
                    setAnnualCost(new Intl.NumberFormat('fr-FR').format(number));
                  }}
                  placeholder="Ex: 1000"
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chiffre d'affaires annuel
                <span className="text-sm text-gray-500 ml-1">(année précédente)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  style={{ display: currentStep.fields.includes('revenue') ? 'block' : 'none' }}
                  value={revenue}
                  onChange={handleRevenueChange}
                  placeholder="Ex: 1000000"
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heures économisées par semaine
                <span className="text-sm text-gray-500 ml-1">(toute l'équipe)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  style={{ display: currentStep.fields.includes('hoursPerWeek') ? 'block' : 'none' }}
                  value={hoursPerWeek}
                  onChange={(e) => setHoursPerWeek(e.target.value)}
                  placeholder="Ex: 5"
                  min="0"
                  max="168"
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coût horaire moyen
                <span className="text-sm text-gray-500 ml-1">(charges incluses)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  style={{ display: currentStep.fields.includes('hourlyRate') ? 'block' : 'none' }}
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="Ex: 50"
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€/h</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secteur d'activité
              </label>
              <select
                style={{ display: currentStep.fields.includes('sector') ? 'block' : 'none' }}
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
              >
                <option value="">Sélectionnez votre secteur</option>
                {industryMultipliers.map(industry => (
                  <option key={industry.sector} value={industry.sector}>
                    {industry.sector}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-orange-50 rounded-lg p-6 mb-10">
            <div className="flex items-center gap-2 mb-2 text-orange-700">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Conseils</span>
            </div>
            <ul className="space-y-2">
              {currentStep.tips.map((tip, i) => (
                <li key={i} className="text-sm text-orange-800 flex items-start gap-2">
                  <span className="text-orange-500">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handleBack}
              disabled={step === 1}
              className={`px-6 py-2 rounded-lg font-medium
                ${step === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Retour
            </button>
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2
                ${!isStepValid()
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
            >
              {step === steps.length ? 'Calculer mon ROI' : 'Suivant'} <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {!showResults && (
            <div className="mt-10 p-6 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-800">
                💡 Ces estimations sont basées sur les résultats moyens observés auprès de nos clients.
                Les résultats réels peuvent varier en fonction de nombreux facteurs.
              </p>
            </div>
          )}
        </div>

        {showResults && estimate && (
          <div className="space-y-8 animate-slideUp xl:pl-8">
            <div className="bg-orange-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <LineChart className="w-6 h-6 text-orange-500" />
                  <h4 className="font-bold">ROI Global</h4>
                </div>
                <span className="text-2xl font-bold text-orange-500">
                  {estimate.roi.toFixed(0)}%
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p>
                  Coût total sur {SIMULATION_YEARS} ans : {formatCurrency(estimate.totalCosts)}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-gray-700" />
                <h4 className="font-bold">Évolution des gains</h4>
              </div>
              <div className="space-y-3">
                {estimate.yearlyGains.map((gain, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Année {index + 1}
                    </span>
                    <span className="font-medium">
                      {formatCurrency(gain)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Target className="w-6 h-6 text-green-500" />
                  <h4 className="font-bold text-lg">Gains cumulés</h4>
                </div>
                <span className="text-2xl font-bold text-green-500">
                  {formatCurrency(estimate.cumulativeGains[SIMULATION_YEARS - 1])}
                </span>
              </div>
              <p className="text-base text-gray-600">
                Total des gains générés sur {SIMULATION_YEARS} ans, incluant :
                <br />• Gains de productivité
                <br />• Nouveaux revenus
                <br />• Croissance annuelle du secteur
              </p>
            </div>

            <a
              href="https://tidycal.com/quentin.tousart/novster-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-4 bg-orange-500 text-white rounded-lg font-semibold text-center hover:bg-orange-600 transition-colors text-lg"
            >
              Réserver un diagnostic gratuit
            </a>
          </div>
        )}
      </div>
    </div>
  );
}